import React, { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { InputSeen, LowQuality, pct } from "./AiEvidence";
import { Time } from "./time";
import { AttemptNote, DIM_OLDER, LatestBadge, latestFirst, newest, unsuccessfulAttempt } from "./Latest";

const AGREE = { agree: ["hs-badge-safe", "Agrees"], partial: ["hs-badge-warning", "Partly agrees"], disagree: ["hs-badge-danger", "Disagrees"] };

function Agreement({ label, value, other }) {
  if (!other) return null;
  const [cls, text] = AGREE[value] || ["hs-badge-neutral", "Not comparable"];
  return <span className={`hs-badge ${cls}`} style={{ marginRight: 6 }}>{text} with {label} ({other})</span>;
}

// A second, independent model's opinion. Its quotes are checked against the exact text it was shown (server-side);
// unverified quotes are struck through and never count as evidence. Never changes a tier.
export default function SecondOpinionPanel({ itemId, styles, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(async () => {
    try { setData((await api.secondOpinions(itemId)).data); } catch (ex) { setMsg("Error: " + ex.message); }
  }, [itemId]);
  useEffect(() => { load(); }, [load]);
  const ask = async () => {
    setBusy(true); setMsg("");
    // The backend answers a provider failure with 424 and a short reason (provider, HTTP status, its message).
    try { await api.askSecondOpinion(itemId); await load(); } catch (ex) { setMsg(ex.message); } finally { setBusy(false); }
  };
  const reask = async () => {
    setBusy(true); setMsg(""); setNote("");
    try {
      const r = (await api.reaskAi(itemId)).data;
      const part = (name, x) => x.status === "ok" ? `${name}: ${x.label || x.verdict}`
        : x.status === "cap" ? `${name}: daily spend cap reached` : x.status === "skipped" ? `${name}: not asked (${x.reason})`
        : `${name}: failed (${x.error || "error"})`;
      setNote(`${part("primary model", r.model)} · ${part("second opinion", r.second_opinion)}`);
      await load();
      if (onChanged) onChanged();
    } catch (ex) { setMsg(ex.message); } finally { setBusy(false); }
  };
  if (!data) return null;
  // The newest usable opinion is "Latest" and comes first. A newer row with an unusable reply, or a newer attempt
  // that stored nothing (cap, provider error: data.last_attempt), is shown next to it in amber.
  const latest = newest(data.opinions.filter((o) => !o.error));
  const newestRow = newest(data.opinions);
  const attempt = unsuccessfulAttempt(data.last_attempt, latest)
    || (newestRow && newestRow.error && newestRow !== latest
      ? { word: "failed", reason: `unusable reply (${newestRow.error})`, at: newestRow.created_at } : null);
  const opinions = latestFirst(data.opinions, [latest?.id]);
  return (
    <div style={styles.card} data-testid="second-opinion-panel">
      <h3 style={{ marginTop: 0 }}>Second opinion</h3>
      <div style={styles.row}>
        <span style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>
          Provider: {data.provider} · {data.model}{data.provider === "mock" ? " (set SECOND_OPINION_PROVIDER for a real model)" : ""}
        </span>
        <button type="button" style={styles.btn} onClick={ask} disabled={busy} data-testid="second-opinion-ask">
          {busy ? "Asking…" : "Ask for a second opinion"}</button>
        <button type="button" style={styles.btnDim} onClick={reask} disabled={busy} data-testid="reask-both"
                title="Re-run the primary model and the second opinion on the current text with the current rules (spend caps apply)">
          Re-ask both AIs</button>
      </div>
      {attempt && !latest && (
        <div style={{ marginTop: 12, fontSize: 12 }}><AttemptNote attempt={attempt} testid="second-opinion-latest-attempt" /></div>)}
      {opinions.map((o) => (
        <div key={o.id} style={{ borderTop: "1px solid var(--hs-border)", marginTop: 12, paddingTop: 12,
                                 ...(o !== latest ? DIM_OLDER : {}), ...(o.outdated ? { opacity: 0.55 } : {}) }}
             data-testid={o.outdated ? "second-opinion-outdated" : "second-opinion"} data-latest={o === latest ? "true" : undefined}>
          {o.outdated && <div style={{ fontSize: 12 }}><span className="hs-badge hs-badge-neutral">{o.outdated.text}</span></div>}
          <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>
            {o === latest && <LatestBadge testid="latest-second-opinion" />}
            <Time value={o.created_at} /> · {o.model} · {o.actor}
            <InputSeen v={o} testid={`opinion-input-${o.id}`} />
            {o === latest && <AttemptNote attempt={attempt} testid="second-opinion-latest-attempt" />}</div>
          {o.error ? <div className="hs-badge hs-badge-warning">Unusable reply: {o.error}</div> : (
            <>
              <div style={{ margin: "6px 0" }}>
                <strong>{o.verdict}</strong> ({pct(o.confidence)})
                <LowQuality v={o} testid={`opinion-low-quality-${o.id}`} />
                {!o.supported && <span className="hs-badge hs-badge-warning" style={{ marginLeft: 6 }}>No verified evidence</span>}
              </div>
              <div>
                <Agreement label="primary model" value={o.agreement?.with_primary} other={o.primary?.label} />
                <Agreement label="reviewer" value={o.agreement?.with_human} other={o.human_label} />
              </div>
              {o.reasoning && <p style={{ fontSize: 13 }}>{o.reasoning}</p>}
              {o.quotes.length > 0 && (
                <ul style={{ fontSize: 12 }}>
                  {o.quotes.map((q, i) => (
                    <li key={i} data-testid={q.verified ? "quote-verified" : "quote-unverified"}>
                      <span style={q.verified ? {} : { textDecoration: "line-through", color: "var(--hs-text-muted)" }}>“{q.quote}”</span>{" "}
                      <span className={`hs-badge ${q.verified ? "hs-badge-safe" : "hs-badge-neutral"}`}>
                        {q.verified ? (q.match === "exact" ? "verbatim" : "verbatim (spacing differs)") : "not in the content"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      ))}
      {note && <div style={{ fontSize: 12, marginTop: 6 }} data-testid="reask-result">{note}</div>}
      {msg && <div className="hs-text-danger" style={{ fontSize: 12, marginTop: 6 }} data-testid="second-opinion-error">{msg}</div>}
    </div>
  );
}
